
	import * as Yup from 'yup';
	import anime from 'animejs/lib/anime.es.js';
	import { computed, defineComponent, onMounted, ref } from 'vue';
	import { ErrorMessage, Field, Form } from 'vee-validate';
	import { Actions } from '@/store/enums/StoreEnums';
	import { clampYearToWorkingYears } from '@/core/helpers/working-years';
	import { getYearFromLocalStorage } from '@/core/helpers/utils';
	import { useStore } from 'vuex';
	import { useRouter } from 'vue-router';
	import { useCustomDialog } from '@/core/hooks/use-custom-dialog';
	import { useLoading } from '@/core/hooks/use-loading';
	import { useButtonCustom } from '@/core/hooks/use-button-custom';

	import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
	import CustomDialog from '@/components/custom-dialog/CustomDialog.vue';

	export default defineComponent({
		name: 'sign-in',
		components: {
			Field,
			Form,
			ErrorMessage,
			ButtonCustom,
			CustomDialog,
		},
		setup() {
			const { isCustomDialogDisplay } = useCustomDialog();
			const { startLoading, endLoading } = useLoading();
			const { ButtonsType, ButtonTypeColors } = useButtonCustom();
			const store = useStore();
			const router = useRouter();
			const workingYears = computed<number[]>(
				() => store.getters.getWorkingYears as number[]
			);
			const curYear = getYearFromLocalStorage();
			const selectedYear = ref<number>(
				Number(
					clampYearToWorkingYears(
						curYear || new Date().getFullYear(),
						workingYears.value
					)
				)
			);

			const titleEl = ref<HTMLElement | null>(null);
			const descEl = ref<HTMLElement | null>(null);
			const fieldYear = ref<HTMLElement | null>(null);
			const fieldPhone = ref<HTMLElement | null>(null);
			const fieldPassword = ref<HTMLElement | null>(null);
			const submitWrap = ref<HTMLElement | null>(null);
			const submitBtn = ref<HTMLElement | null>(null);

			const phone = ref();
			const password = ref();

			const login = Yup.object().shape({
				phone: Yup.string()
					.required('Vui lòng nhập tài khoản')
					.label('Tài khoản'),
				password: Yup.string()
					.required('Vui lòng nhập mật khẩu')
					.min(4, 'Mật khẩu phải có ít nhất 4 ký tự')
					.label('Mật khẩu'),
			});

			const onSubmitLogin = async () => {
				if (submitBtn.value) {
					anime({
						targets: submitBtn.value,
						scale: [1, 0.96, 1],
						duration: 300,
						easing: 'easeOutQuad',
					});
				}

				startLoading();

				await store.dispatch(Actions.LOGIN, {
					phone: phone.value,
					password: password.value,
				});

				endLoading();
				const error = store.getters.getErrors;

				if (!error) {
					const year = clampYearToWorkingYears(
						selectedYear.value,
						store.getters.getWorkingYears as number[]
					);
					selectedYear.value = Number(year);

					await store.dispatch(Actions.INIT_USER_SYSTEM_SETTINGS, {
						year,
					});
					await router.replace({ name: 'dashboard' });
				} else {
					isCustomDialogDisplay.value = true;
				}
			};

			onMounted(() => {
				requestAnimationFrame(() => {
					const tl = anime.timeline({ easing: 'easeOutExpo' });

					if (titleEl.value) {
						tl.add({
							targets: titleEl.value,
							opacity: [0, 1],
							translateY: [20, 0],
							duration: 600,
						});
					}

					if (descEl.value) {
						tl.add(
							{
								targets: descEl.value,
								opacity: [0, 1],
								translateY: [16, 0],
								duration: 500,
							},
							'-=350',
						);
					}

					const fields = [
						fieldYear.value,
						fieldPhone.value,
						fieldPassword.value,
					].filter(Boolean) as HTMLElement[];

					if (fields.length) {
						tl.add(
							{
								targets: fields,
								opacity: [0, 1],
								translateY: [24, 0],
								duration: 500,
								delay: anime.stagger(100),
							},
							'-=300',
						);
					}

					if (submitWrap.value) {
						tl.add(
							{
								targets: submitWrap.value,
								opacity: [0, 1],
								translateY: [20, 0],
								duration: 600,
							},
							'-=200',
						);
					}
				});
			});

			return {
				ButtonsType,
				ButtonTypeColors,
				onSubmitLogin,
				login,
				workingYears,
				selectedYear,
				isCustomDialogDisplay,
				phone,
				password,
				titleEl,
				descEl,
				fieldYear,
				fieldPhone,
				fieldPassword,
				submitWrap,
				submitBtn,
			};
		},
	});
